import {Language} from '../i18nTypes';
import {Option} from '../common/select/select';
import {HibuFacetFilter, HibuFacetGroup} from '../facets/facet.types';

export type SearchSource = {
    readonly name: string;
    readonly active: boolean;
};

// this class acts like an enum, but is able to handle objects.
// https://stackoverflow.com/questions/41179474/use-object-literal-as-typescript-enum-values
export class SearchSortType {
    static readonly RELEVANCE = new SearchSortType('score', 'desc');
    static readonly NAME = new SearchSortType('title', 'asc');
    static readonly PATH = new SearchSortType('contentSource', 'asc');
    static readonly LAST_INDEXED = new SearchSortType('metadata.lastIndex_date', 'desc');
    static readonly RANDOM = new SearchSortType('random', 'random');

    // private to disallow creating other instances of this type
    private constructor(
        public readonly field: string,
        public readonly order: any,
    ) {
    }

    static getAll(): SearchSortType[] {
        return [SearchSortType.RELEVANCE, SearchSortType.NAME, SearchSortType.PATH, SearchSortType.LAST_INDEXED];
    }

    toString() {
        return this.field;
    }
}

export type SortType = {
    field: string;
    order: string;
};

export interface SortOption extends Option {
    order: string;
}

export enum SearchSourceType {
    BOOKMARKS = 'bookmarks',
    ALL_DOCUMENTS = 'allDocuments',
}

export enum SearchExportFormat {
    HTML = 'html',
    JSON = 'json',
}

export type HibuSearchRequestSort = {
    readonly order?: string;
    readonly field?: string;
    readonly seed?: string;
};

export type HibuSearchResponse = {
    allSuccessful: boolean;
    totalFound: number;
    resultFor?: string;
    totalFoundOriginalQuery?: number;
    maxScore: number;
    hits: HibuHit[];
    facets: HibuFacetGroup[];
    spellcheckSuggestions: HibuSpellcheckSuggestion[];
    searchRequest: HibuSearchRequest;
    pointInTime?: string;
    searchExecutionDurationMs: number;
};

export type HibuHighlightFields = {
    key: string;
    fragments: string[];
};

export type HibuHit = {
    score: number;
    doc: HibuHitDoc;
    highlightFields: HibuHighlightFields[];
};

export type HibuHitDoc = {
    id: string;
    contentSource: string;
    language: string;
    title: string;
    metadata: MetaDataGenericList;
    tags: TagMap;
    indexVersion: string;
    duplicates: HibuDocumentDuplicates[];
};

export type HibuSpellcheckSuggestion = {
    text: string;
    score: number;
    freq: number;
};

export type HibuSearchRequestSpellcheckParams = {
    readonly spellcheckSuggestionMinDocFreq: number;
    readonly spellcheckSuggestionMinDocFreqGain: number;
    readonly autoCorrectionMinDocFreq?: number;
    readonly autoCorrectionMinDocFreqGain?: number;
    readonly autoCorrectionOriginalQueryMaxDocFreq?: number;
};

export type SearchRequest = HibuSearchRequest & {
    isSearchInBookmarks?: boolean;
};

export type HibuSearchRequest = {
    readonly container: string;
    readonly displayLanguage: string;
    readonly query: string;
    readonly from: number;
    readonly size: number;
    readonly pointInTime?: string;
    readonly filters: HibuFacetFilter;
    readonly sort: HibuSearchRequestSort;
    readonly spellcheckEnabled: boolean;
    readonly spellcheckParams: HibuSearchRequestSpellcheckParams;
    facetFields?: string[];
    queryFields?: string[];
    returnFields?: ReturnFields;
};

export type SearchResult = HibuSearchResponse & {
    docs: ProdDocument[];
    nextDocOffset: number;
    shouldReplaceSearchResults: boolean;
    lastSearchedQuery: string;
    spellCheckResult: string;
};

export interface BaseMetadata {
    creation: string;
    description: string;
}

export type Highlights = Partial<{
    title: string[];
    contentSource: string[];
    content: string[];
}>;

export type Aggregations = Record<string, Aggregation[]>;

export interface Aggregation {
    values: AggregationItems;
    aggregationIndex?: number;
}

export type AggregationItems = Record<string, string[]>;

export type HibuTags = Record<string, HibuTag[]>;

export interface HibuTag {
    id: string;
    label: string;
    spans?: Span[];
    extra?: Extra;
}

export type HibuDocumentDuplicates = {
    id: string;
    contentSource: string;
};

export type MetaDataGenericList = Record<string, any>;

export interface MetaDataList extends MetaDataGenericList {
    //Warning: do not rename these properties, because otherwise it won't be mapped from the backend
    downloadLink_string: string;
    previewLink_string: string;
    contentType_string: string;
    contentUri_string: string;
    bookmarked: boolean;
}

export interface Span {
    start?: number;
    end?: number;
    pageNumber: number;
    bottomLeftX: number;
    bottomLeftY: number;
    width: number;
    height: number;
    pageWidth: number;
    pageHeight: number;
}

export type Extra = Record<string, any>;

export type TagMap = {
    [key: string]: TagMapEntry[];
};

export type TranslatedLabels = Record<Language, string>;

export type TagMapEntry = {
    tagId: string;
    labels: TranslatedLabels;
    spans: Span[];
    extra?: Extra;
};

export type ProdDocument = ProductDocument;

export type ReturnFields = {
    includes?: string[];
    excludes?: string[];
};

export interface ProductDocument extends BaseMetadata {
    aggregations: Aggregations;
    id: string;
    language: string;
    title: string;
    contentSource: string;
    metadata: MetaDataList;
    content: string;
    highlights: Highlights;
    hibuTags: HibuTags;
    duplicates: HibuDocumentDuplicates[];
}

export interface TagUpdate {
    tagType: string;
    tagId: string | null;
    extra: { [key: string]: any } | null;
    labels: { [language: string]: string } | null;
    spans?: Span[];
}

export enum TabType {
    CONTENT = 'CONTENT',
    SPOTLIGHT = 'SPOTLIGHT',
    DETAILS = 'DETAILS',
    HIBU_ASSISTANT = 'HIBU_ASSISTANT',
}

export enum ActiveDrawerContent {
    PLACEHOLDER = 'PLACEHOLDER',
    ASSISTANT = 'ASSISTANT',
    PREVIEW = 'PREVIEW',
}
